<template>
  <section class="py-12 mx-auto">
    <!-- Header -->
    <div class="flex flex-col md:flex-row items-center gap-8">
      <div class="md:w-[50%] 2xl:w-[60%]">
        <h2 class="headline">{{ sectionTitle }}</h2>
        <p class="sub-headline">{{ sectionDescription }}</p>
      </div>
      <div v-if="currentTab" class="flex justify-center md:w-[50%] 2xl:w-[40%]">
        <img
          :src="`${currentTab.sourceUrl}`"
          :alt="currentTab.title"
          class="tab-image"
        />
      </div>
    </div>

    <!-- Tabs -->
    <div
      ref="tabContainer"
      class="mt-12 relative grid grid-flow-col auto-cols-[minmax(200px,_1fr)] overflow-x-auto scrollbar-hide scroll-smooth"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :ref="(el) => (tabRefs[index] = el)"
        @click="selectTab(tab)"
        :class="[
          'tab-item w-full max-w-full px-2 py-2 text-left',
          { active: index === tabs.indexOf(currentTab) },
        ]"
      >
        <h4 class="tab-title">{{ tab.title }}</h4>
        <p class="tab-description">{{ tab.description }}</p>
      </div>

      <!-- Active Tab Indicator -->
      <div
        class="tab-indicator absolute top-0 h-[4px] bg-[#8a1f2d] transition-all duration-300"
        :style="indicatorStyle"
      />
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue";
import commonService from "@/services/commonService";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const sectionTitle = ref("");
const sectionDescription = ref("");
const tabs = ref([]);
const currentTab = ref(null);
const tabContainer = ref(null);
const tabRefs = ref([]);
const indicatorStyle = ref({ left: "0px", width: "0px" });
const AUTO_TAB_INTERVAL = ref(5000);

let autoScrollTimer = null;
let autoTabTimer = null;

const SCROLL_SPEED = 1.2;
const SCROLL_INTERVAL = 30;

const defaultTabs = [
  {
    title: "Generate Estimates Instantly",
    description: "Build accurate estimates with real-time pricing.",
  },
  {
    title: "Customize & Send Proposals",
    description: "Impress clients with professional, branded proposals.",
  },
  {
    title: "Use Ready-Made Templates",
    description: "Save time with pre-built estimate and order templates.",
  },
  {
    title: "Track & Manage Orders",
    description: "Stay updated on material delivery from start to finish.",
  },
];

function selectTab(tab) {
  currentTab.value = tab;
  nextTick(updateIndicator);
  // tabContainer.value.scrollTo({ left: el.offsetLeft - 20, behavior: "smooth" });
}

function updateIndicator() {
  const index = tabs.value.indexOf(currentTab.value);
  const el = tabRefs.value[index];
  if (el) {
    indicatorStyle.value = {
      left: `${el.offsetLeft}px`,
      width: `${el.offsetWidth}px`,
    };
  }
}

function startAutoScroll() {
  stopAutoScroll();
  autoScrollTimer = setInterval(() => {
    const container = tabContainer.value;
    if (!container) return;
    const atEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth;
    container.scrollTo({
      left: atEnd ? 0 : container.scrollLeft + SCROLL_SPEED,
      behavior: "smooth",
    });
  }, SCROLL_INTERVAL);
}

function stopAutoScroll() {
  clearInterval(autoScrollTimer);
  autoScrollTimer = null;
}

function startAutoTabSwitch() {
  stopAutoTabSwitch();
  autoTabTimer = setInterval(() => {
    const index = tabs.value.indexOf(currentTab.value);
    const nextIndex = (index + 1) % tabs.value.length;
    selectTab(tabs.value[nextIndex]);
  }, AUTO_TAB_INTERVAL.value);
}

function stopAutoTabSwitch() {
  clearInterval(autoTabTimer);
  autoTabTimer = null;
}

function handleMouseEnter() {
  stopAutoScroll();
  stopAutoTabSwitch();
}

function handleMouseLeave() {
  startAutoScroll();
  startAutoTabSwitch();
}

const fetchTabs = async () => {
  const lang = locale.value === "es" ? "es-US" : locale.value;

  try {
    const res = await commonService.fetchTabbedMarketingTabs(lang);
    const data = res?.items || [];

    tabs.value = data.length ? data : defaultTabs;
    currentTab.value = tabs.value[0];

    sectionTitle.value =
      res?.title ||
      "From Estimates to Delivery—Seamless Roofing Supply Management with Roof Hub";
    sectionDescription.value =
      res?.description ||
      "Roof Hub streamlines your workflow with Estimates, professional proposals, and ready-to-use templates—making roofing supply management faster and easier than ever.";
    AUTO_TAB_INTERVAL.value = res?.autoScrollInterval || 5000;
  } catch {
    tabs.value = defaultTabs;
    currentTab.value = defaultTabs[0];
    sectionTitle.value =
      "From Estimates to Delivery—Seamless Roofing Supply Management with Roof Hub";
    sectionDescription.value =
      "Roof Hub streamlines your workflow with Estimates, professional proposals, and ready-to-use templates—making roofing supply management faster and easier than ever.";
  }
};

onMounted(async () => {
  fetchTabs();
  await nextTick();
  updateIndicator();
  startAutoScroll();
  startAutoTabSwitch();
});

onBeforeUnmount(() => {
  stopAutoScroll();
  stopAutoTabSwitch();
});

// 🟡 Re-fetch when locale changes
watch(locale, () => {
  fetchTabs();
});
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.tab-item {
  min-width: 200px;
  cursor: pointer;
  scroll-snap-align: start;
  border-top: 4px solid #d1d1d1;
  transition: border-color 0.3s ease-in-out;
}

.tab-item.active .tab-title {
  color: #762123;
}

.tab-indicator {
  position: absolute;
  top: 0;
  height: 4px;
  background-color: #8a1f2d;
  transition: all 0.6s ease;
}

.tab-image {
  max-width: 100%;
}

.headline {
  font-size: 39px;
  font-weight: 500;
  text-align: left;
  color: #2e2e2e;
  padding-bottom: 15px;
  line-height: 1.3;
}

.sub-headline {
  opacity: 0.8;
  font-size: 15px;
  text-align: left;
  color: #2e2e2e;
  line-height: 1.5;
}

.tab-title {
  font-size: 14px;
  font-weight: bold;
  text-align: left;
  color: #2e2e2e;
  line-height: 1.5;
  padding-bottom: 5px;
}

.tab-description {
  opacity: 0.8;
  font-size: 14px;
  text-align: left;
  color: #2e2e2e;
}

@media (max-width: 576px) {
  .headline {
    font-size: 26px;
    font-weight: 600;
  }
  .tab-title {
    line-height: inherit;
  }
}
</style>
